import { StagingBanner } from '@a7y/ui/staging-banner';
import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { ConfirmDialogProvider } from '@/contexts/ConfirmDialogContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AdminLayout } from '@/layouts/AdminLayout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { OrganizationDetailPage } from '@/pages/OrganizationDetailPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { UsersPage } from '@/pages/UsersPage';
import { UserDetailPage } from '@/pages/UserDetailPage';
import { BillingPage } from '@/pages/BillingPage';
import { SubscriptionsPage } from '@/pages/SubscriptionsPage';
import { SubscriptionDetailPage } from '@/pages/SubscriptionDetailPage';
import { SystemPage } from '@/pages/SystemPage';
import { AuditLogsPage } from '@/pages/AuditLogsPage';
import { FeatureFlagsPage } from '@/pages/FeatureFlagsPage';
import { PromoCodesPage } from '@/pages/PromoCodesPage';
import { ShortLinksPage } from '@/pages/short-links/ShortLinksPage';
import ProductEmailPage from '@/pages/product-email/ProductEmailPage';
import SendDetailPage from '@/pages/product-email/SendDetailPage';
import { ErrorsWarningsPage } from '@/pages/ErrorsWarningsPage';
import { ErrorWarningDetailPage } from '@/pages/ErrorWarningDetailPage';
import { AuditTestingPage } from '@/pages/audit-testing/AuditTestingPage';
import { AuditTestDetailPage } from '@/pages/audit-testing/AuditTestDetailPage';
import { RunGroupDetailPage } from '@/pages/audit-testing/RunGroupDetailPage';
import { ServiceHealthPage } from '@/pages/ServiceHealthPage';
import { PlatformHealthOverviewPage } from '@/pages/platform-health/OverviewPage';
import { PlatformHealthCriticalPage } from '@/pages/platform-health/CriticalPage';
import { StagingDeploysPage } from '@/pages/platform-health/StagingDeploysPage';
import { ProdDeploysPage } from '@/pages/platform-health/ProdDeploysPage';
import { TestRunsPage } from '@/pages/platform-health/TestRunsPage';
import { PulseOperationsPage } from '@/pages/platform-health/PulseOperationsPage';
import { MantisWorkbenchPage } from '@/pages/platform-health/MantisWorkbenchPage';
import { AquixWorkbenchPage } from '@/pages/platform-health/AquixWorkbenchPage';
import { AuditAssertionsPage } from '@/pages/platform-health/AuditAssertionsPage';
import { PrismWorkbenchPage } from '@/pages/platform-health/PrismWorkbenchPage';
import { CredibilityDriftPage } from '@/pages/platform-health/CredibilityDriftPage';
import AphidStatusPage from '@/pages/platform-health/AphidStatusPage';
import { AhcPage } from '@/pages/autobuild/RoutinePage';
import { StateInvariantsPage } from '@/pages/platform-health/StateInvariantsPage';
import { EmailDeliveryPage } from '@/pages/platform-health/EmailDeliveryPage';
import { AuditSpendPage } from '@/pages/AuditSpend';
import { ChannelConsolePage } from '@/pages/ChannelConsole';
import { HelmPage } from '@/pages/cmo/helm/HelmPage';
import { CrmPage } from '@/pages/cmo/crm/CrmPage';
import { CrmAccountPage } from '@/pages/cmo/crm/CrmAccountPage';
import { CrmWeekPage } from '@/pages/cmo/crm/CrmWeekPage';
import { CrmBoardPage } from '@/pages/cmo/crm/CrmBoardPage';
import { automationRoutes } from '@/pages/autobuild/automationRoutes';
import { aiApisRoutes } from '@/pages/ai-apis/aiApisRoutes';
import { SaasSpend } from '@/pages/SaasSpend';
import { PnlPage } from '@/pages/finance/PnlPage';
import { PnlMonthDetailPage } from '@/pages/finance/PnlMonthDetailPage';
import { KeywordDatabasePage } from '@/pages/stats/KeywordDatabasePage';
import { StatsOverviewPage } from '@/pages/stats/StatsOverviewPage';
import { lazy, Suspense } from 'react';
import { AdminMessagingProvider } from '@/contexts/AdminMessagingContext';
import { EmailSuppressionsPage } from '@/pages/email-suppressions/EmailSuppressionsPage';
import { TrialEligibilityPage } from '@/pages/TrialEligibilityPage';
import { AnnouncementsPage } from '@/pages/announcements/AnnouncementsPage';
import { ReleaseNotesPage } from '@/pages/release-notes/ReleaseNotesPage';
import { BlogListPage } from '@/pages/content/blog/BlogListPage';
import { PublishingPage } from '@/pages/content/PublishingPage';
import LearningTopicsPage from '@/pages/learning-topics/LearningTopicsPage';
import { UtilsPage } from '@/pages/utils/UtilsPage';
import { CapitalIndexNotFound, CapitalRouter } from '@/capital/CapitalRouter';
import { isInvestorHost } from '@/capital/investor-host';
import { AffiliatesPage } from '@/pages/affiliates/AffiliatesPage';
import { AffiliateDetailPage } from '@/pages/affiliates/AffiliateDetailPage';
import { CampaignsPage } from '@/pages/affiliates/CampaignsPage';
import { CommissionsPage } from '@/pages/affiliates/CommissionsPage';
import { PayoutsPage } from '@/pages/affiliates/PayoutsPage';
import { SuspiciousQueuePage } from '@/pages/affiliates/SuspiciousQueuePage';
import { FirstCommissionQueuePage } from '@/pages/affiliates/FirstCommissionQueuePage';
import { ReconciliationPage } from '@/pages/affiliates/ReconciliationPage';
import { ProgramOverviewPage } from '@/pages/affiliates/ProgramOverviewPage';

const MessagingListPage = lazy(() => import('@/pages/messaging/MessagingListPage'));
const MessagingDetailPage = lazy(() => import('@/pages/messaging/MessagingDetailPage'));
const BlogEditorPage = lazy(() => import('@/pages/content/blog/BlogEditorPage'));
const ContentKeysPage = lazy(() => import('@/pages/content/ContentKeysPage'));
// A7Y-4470: docs CMS — lazy like the blog editor above.
const DocsPage = lazy(() => import('@/pages/content/docs/DocsPage'));
const DocsEditorPage = lazy(() => import('@/pages/content/editor/sections/docs'));
const FeedbackPage = lazy(() => import('@/pages/content/FeedbackPage'));
// Dev-only: the devtools are a devDependency and never reach the production bundle.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools }))
    )
  : null;

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function LegacyAutobuildRedirect() {
  const { kind = '', stamp = '' } = useParams();
  return <Navigate to={`/admin/automation/${kind}/${stamp}`} replace />;
}

/**
 * A7Y-4474: the blog editor moved under /admin/content/blog. `postId` is
 * substituted when the legacy route carried one; the search string (filters,
 * `?status=`, etc.) is preserved so a bookmarked or filtered legacy link keeps
 * working at its new path.
 */
function LegacyBlogRedirect({ to }: { to: string }) {
  const { postId } = useParams();
  const location = useLocation();
  const target = postId ? to.replace(':postId', postId) : to;
  return <Navigate to={`${target}${location.search}`} replace />;
}

function AdminApp() {
  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      afterSignOutUrl="/admin/login"
      signInFallbackRedirectUrl="/admin"
      signInForceRedirectUrl="/admin"
      telemetry={false}
      appearance={{ baseTheme: dark }}
    >
      <ThemeProvider>
        <AdminAuthProvider>
          <AdminMessagingProvider>
            <ConfirmDialogProvider>
              <StagingBanner />
              <Routes>
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="organizations" element={<OrganizationsPage />} />
                  <Route path="organizations/:orgId" element={<OrganizationDetailPage />} />
                  <Route
                    path="organizations/:orgId/projects/:projectId"
                    element={<ProjectDetailPage />}
                  />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="users/:userId" element={<UserDetailPage />} />
                  <Route path="feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="subscriptions" element={<SubscriptionsPage />} />
                  <Route
                    path="subscriptions/:subscriptionId"
                    element={<SubscriptionDetailPage />}
                  />
                  <Route path="support" element={<Navigate to="/admin/messaging" replace />} />
                  <Route
                    path="messaging"
                    element={
                      <Suspense fallback={null}>
                        <MessagingListPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="messaging/:threadId"
                    element={
                      <Suspense fallback={null}>
                        <MessagingDetailPage />
                      </Suspense>
                    }
                  />
                  <Route path="system" element={<SystemPage />} />
                  <Route path="audit-logs" element={<AuditLogsPage />} />
                  <Route path="promo-codes" element={<PromoCodesPage />} />
                  <Route path="short-links" element={<ShortLinksPage />} />
                  <Route path="product-emails" element={<ProductEmailPage />} />
                  <Route path="email-suppressions" element={<EmailSuppressionsPage />} />
                  <Route path="trial-eligibility" element={<TrialEligibilityPage />} />
                  <Route path="affiliates" element={<AffiliatesPage />} />
                  <Route path="affiliates/overview" element={<ProgramOverviewPage />} />
                  <Route path="affiliates/campaigns" element={<CampaignsPage />} />
                  <Route path="affiliates/commissions" element={<CommissionsPage />} />
                  <Route path="affiliates/payouts" element={<PayoutsPage />} />
                  <Route path="affiliates/suspicious" element={<SuspiciousQueuePage />} />
                  <Route path="affiliates/first-hold" element={<FirstCommissionQueuePage />} />
                  <Route path="affiliates/reconciliation" element={<ReconciliationPage />} />
                  <Route path="affiliates/:affiliateId" element={<AffiliateDetailPage />} />
                  <Route path="announcements" element={<AnnouncementsPage />} />
                  <Route path="release-notes" element={<ReleaseNotesPage />} />
                  <Route
                    path="content/keys"
                    element={
                      <Suspense fallback={null}>
                        <ContentKeysPage />
                      </Suspense>
                    }
                  />
                  {/* A7Y-4474: the CONTENT section — Publishing, Docs (placeholder)
                      and the moved Blog CMS. `/admin/content` itself resolves to
                      the Publishing queue, the section's default landing page. */}
                  <Route
                    path="content"
                    element={<Navigate to="/admin/content/publishing" replace />}
                  />
                  <Route path="content/publishing" element={<PublishingPage />} />
                  <Route
                    path="content/docs"
                    element={
                      <Suspense fallback={null}>
                        <DocsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="content/docs/new"
                    element={
                      <Suspense fallback={null}>
                        <DocsEditorPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="content/docs/:pageId"
                    element={
                      <Suspense fallback={null}>
                        <DocsEditorPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="content/feedback"
                    element={
                      <Suspense fallback={null}>
                        <FeedbackPage />
                      </Suspense>
                    }
                  />
                  <Route path="content/blog" element={<BlogListPage />} />
                  <Route
                    path="content/blog/new"
                    element={
                      <Suspense fallback={null}>
                        <BlogEditorPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="content/blog/:postId"
                    element={
                      <Suspense fallback={null}>
                        <BlogEditorPage />
                      </Suspense>
                    }
                  />
                  {/* Legacy /admin/blog* links (bookmarks, Slack, emails) redirect
                      to their new home under /admin/content/blog, preserving any
                      query string (filters, etc.). */}
                  <Route
                    path="blog"
                    element={<LegacyBlogRedirect to="/admin/content/blog" />}
                  />
                  <Route
                    path="blog/new"
                    element={<LegacyBlogRedirect to="/admin/content/blog/new" />}
                  />
                  <Route
                    path="blog/:postId"
                    element={<LegacyBlogRedirect to="/admin/content/blog/:postId" />}
                  />
                  <Route path="learning-topics" element={<LearningTopicsPage />} />
                  <Route path="product-emails/sends/:sendId" element={<SendDetailPage />} />
                  <Route path="errors-warnings" element={<ErrorsWarningsPage />} />
                  <Route path="errors-warnings/:messageId" element={<ErrorWarningDetailPage />} />
                  <Route path="audit-testing" element={<AuditTestingPage />} />
                  <Route path="audit-testing/group/:runGroupId" element={<RunGroupDetailPage />} />
                  <Route path="audit-testing/:testRunId" element={<AuditTestDetailPage />} />
                  <Route path="service-health" element={<ServiceHealthPage />} />
                  <Route path="platform-health" element={<PlatformHealthOverviewPage />} />
                  <Route path="platform-health/critical" element={<PlatformHealthCriticalPage />} />
                  <Route path="platform-health/staging-deploys" element={<StagingDeploysPage />} />
                  <Route
                    path="platform-health/raven"
                    element={<Navigate to="/admin/platform-health/staging-deploys" replace />}
                  />
                  <Route path="platform-health/prod-deploys" element={<ProdDeploysPage />} />
                  <Route path="platform-health/test-runs" element={<TestRunsPage />} />
                  <Route path="platform-health/aphid" element={<AphidStatusPage />} />
                  {/* AHC's page moved here from automation/ahc; that path now
                      redirects. The component is unchanged. */}
                  <Route path="platform-health/ahc" element={<AhcPage />} />
                  <Route path="platform-health/pulse" element={<PulseOperationsPage />} />
                  <Route path="platform-health/mantis" element={<MantisWorkbenchPage />} />
                  <Route path="platform-health/aquix" element={<AquixWorkbenchPage />} />
                  <Route
                    path="platform-health/audit-assertions"
                    element={<AuditAssertionsPage />}
                  />
                  <Route path="platform-health/prism" element={<PrismWorkbenchPage />} />
                  {/* A7Y-2330: the Expert Eye placeholder is superseded by the PRISM
                      workbench; the old path redirects so bookmarks keep working. */}
                  <Route
                    path="platform-health/expert-eye"
                    element={<Navigate to="/admin/platform-health/prism" replace />}
                  />
                  <Route path="platform-health/credibility" element={<CredibilityDriftPage />} />
                  <Route
                    path="platform-health/state-invariants"
                    element={<StateInvariantsPage />}
                  />
                  <Route path="platform-health/email-delivery" element={<EmailDeliveryPage />} />
                  <Route path="audit-spend" element={<AuditSpendPage />} />
                  <Route path="channel-console" element={<ChannelConsolePage />} />
                  <Route path="cmo/helm" element={<HelmPage />} />
                  <Route path="cmo/crm" element={<CrmPage />} />
                  <Route path="cmo/crm/this-week" element={<CrmWeekPage />} />
                  <Route path="cmo/crm/board" element={<CrmBoardPage />} />
                  <Route path="cmo/crm/:accountId" element={<CrmAccountPage />} />
                  {/* Order is load-bearing (literal segments before
                      automation/:kind/:stamp) and is asserted in
                      pages/autobuild/automationRoutes.test.tsx. */}
                  {automationRoutes}
                  {/* Legacy /admin/autobuild links (Slack reports, bookmarks) */}
                  <Route path="autobuild" element={<Navigate to="/admin/automation" replace />} />
                  <Route path="autobuild/:kind/:stamp" element={<LegacyAutobuildRedirect />} />
                  <Route path="saas-spend" element={<SaasSpend />} />
                  <Route path="finance/pnl" element={<PnlPage />} />
                  <Route path="finance/pnl/:month" element={<PnlMonthDetailPage />} />
                  {aiApisRoutes}
                  <Route path="stats/overview" element={<StatsOverviewPage />} />
                  <Route path="stats/keyword-database" element={<KeywordDatabasePage />} />
                  <Route path="utils" element={<UtilsPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </ConfirmDialogProvider>
          </AdminMessagingProvider>
        </AdminAuthProvider>
      </ThemeProvider>
    </ClerkProvider>
  );
}

function App() {
  // /era/* (the investor deck) renders outside the admin provider stack — no
  // Clerk auth, no AdminMessaging SSE, no ConfirmDialog/Theme/AdminAuth. The
  // deck is fully public and provider-free apart from the global
  // QueryClientProvider. On the ir host the admin app never mounts at all: the
  // asset layer answers a navigation with this same shell, whatever the path.
  const investorHost = isInvestorHost(window.location.hostname);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/era/*" element={<CapitalRouter />} />
          <Route path="*" element={investorHost ? <CapitalIndexNotFound /> : <AdminApp />} />
        </Routes>
      </BrowserRouter>
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

export default App;
